<template>
    <div v-if="migrationComplete" class="alert alert-success">
        {{ $t("Location migration completed successfully!") }}
    </div>
    <div class="row align-items-end mb-3">
        <div class="col-sm mb-2 mb-sm-0">
            <h1 class="page-header-title">{{ $t("Organizations") }}</h1>
        </div>

        <div class="col-sm-auto">
            <AppButton @click="toggleAdd">
                <i class="bi-plus-circle me-1"></i> {{ $t("Add organization") }}
            </AppButton>
        </div>
    </div>

    <AppModal
        v-model="isAddOrganizationVisible"
        size="xl"
        ref="orgModalRef"
    >
        <template #default>
            <OrgCreate
                v-if="!showMigrate && !showPatientMigrate"
                @toggle-to-migrate="toggleMigrate"
                @saving-updated="toggleSaving"
                @organization-created="organizationCreated"
                @send-patient-data="receivePatientData"
                :new-location-data="newLocationData"
                ref="orgCreate"
                :key="dtKey"
            />

            <OrgMigrate
                v-if="showMigrate"
                @back="toggleMigrate"
                @update-location="updateLocation"
                ref="orgCreate"
            />
        </template>

        <template #header>
            <h3 v-if="!showMigrate && !showPatientMigrate" class="modal-title">{{ $t("Create Organization") }}</h3>
            <h3 v-if="showMigrate" class="modal-title">{{ $t("Migrate Organization") }}</h3>
            <h3 v-if="showPatientMigrate" class="modal-title">{{ $t("Migrate Patients") }}</h3>
        </template>

        <template #footer>
            <AppButton
                v-if="!showMigrate && !showPatientMigrate"
                btnClass="btn-white"
                @click="closeOrgModal"
            >
                <i class="bi-x-lg me-1"></i> {{ $t("Close") }}
            </AppButton>
            <AppButton
                v-if="!showMigrate && !showPatientMigrate"
                @click="submitOrgForm"
                :is-loading="isSavingOrganization"
            >
                <i class="bi-save me-1"></i> {{ $t("Add organization") }}
            </AppButton>
            <AppButton
                v-if="showMigrate"
                @click="toggleMigrate"
                btnClass="btn-white"
            >
                <i class="bi-save me-1"></i> {{ $t("Go Back") }}
            </AppButton>
            <AppButton
                v-if="showPatientMigrate || showMigrate"
                @click="closeAllModals"
                btnClass="btn-white"
            >
                <i class="bi-save me-1"></i> {{ $t("Cancel") }}
            </AppButton>
        </template>
    </AppModal>


    <AppModal
        ref="patientModalRef"
        v-model="showPatientMigrate"
        size="xl"
        :closeButton="false"
    >
        <template #default>
            <OrgCreateMigratePatients
                v-if="showPatientMigrate"
                @close-modals="closeAllModals"
                @migration-complete="organizationCreated"
                :active-patients="receivedActivePatients"
                :inactive-patients="receivedInactivePatients"
                :org="newLocationData"
            />
        </template>

        <template #header>
            <h3 v-if="showPatientMigrate" class="modal-title">{{ $t("Migrate Patients") }}</h3>
        </template>

        <template #footer>
            <AppButton
                v-if="showPatientMigrate"
                @click="closeAllModals"
                btnClass="btn-white"
            >
                <i class="bi-save me-1"></i> {{ $t("Cancel") }}
            </AppButton>
        </template>
    </AppModal>

    <DataTable
        id="orgs"
        :url="`/api/orgs/datatable`"
        :columns="columns"
        :filters="appliedFilters"
        :hideTableActions="true"
    >
        <template #filters>
            <div class="col-auto p-1">
                <AppButton
                    btnClass="btn-white btn-sm"
                    @click="() => isFiltersVisible = true"
                >
                    <i class="bi-filter me-1"></i> {{ $t("Filters") }} <span
                    class="badge bg-soft-dark text-dark rounded-circle ms-1">{{ filtersCount }}</span>
                </AppButton>
            </div>
            <div class="col-auto p-1" v-if="filtersCount >= 1">
                <AppButton
                    btnClass="btn-white btn-sm"
                    @click="resetFilters"
                >
                    <i class="bi-arrow-counterclockwise"></i> {{ $t("Reset") }}
                </AppButton>
            </div>
            <AppOffcanvas
                :title="$t('Filters')"
                :isVisible="isFiltersVisible"
                @update:is-visible="isFiltersVisible = $event"
            >
                <template #body>

                    <div class="row">
                        <div class="col-12 mb-4">
                            <label for="active" class="form-label text-cap text-body">{{ $t("Type") }}</label>

                            <select
                                name="type"
                                class="form-select"
                                tabindex="-1"
                                v-model="formValues.type"
                            >
                                <option value="" selected>{{ $t("Any") }}</option>
                                <option
                                    v-for="{id, is_clinical, type} in orgTypes"
                                    :key="id" :value="id"
                                    :data-is-clinical="is_clinical">
                                    {{ type }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 mb-4">
                            <label for="active" class="form-label text-cap text-body">{{ $t("Status") }}</label>

                            <select
                                name="status"
                                class="form-select"
                                tabindex="-1"
                                v-model="formValues.status"
                            >
                                <option value="" selected>{{ $t("All") }}</option>
                                <option value="0">{{ $t("Inactive") }}</option>
                                <option value="1">{{ $t("Active") }}</option>

                            </select>
                        </div>
                    </div>
                    <hr>
                </template>
                <template #footer>
                    <div class="col">
                        <div class="d-grid">
                            <AppButton @click="cleanForm" btnClass="btn-white">
                                <i class="bi bi-arrow-counterclockwise"></i> {{ $t("Reset form") }}
                            </AppButton>
                        </div>
                    </div>

                    <div class="col">
                        <div class="d-grid">
                            <AppButton @click="applyFilters">
                                <i class="bi bi-check-square"></i> {{ $t("Apply") }}
                            </AppButton>
                        </div>
                    </div>
                </template>
            </AppOffcanvas>
        </template>

        <template #selectedFilters>
            <ul class="list-unstyled mb-0">
                <li class="d-inline-block" v-if="appliedFilters.type !== ''">
                    <span class="filter">
                        {{ orgTypes.find((type) => type.id === appliedFilters.type).type }}
                        <span class="bi bi-x clean-filter" @click="cleanFilterAndApply('type')"></span>
                    </span>
                </li>

                <li class="d-inline-block" v-if="appliedFilters.status !== ''">
                    <span class="filter">
                        {{ appliedFilters.status === "0" ? $t("Inactive") : $t("Active") }}
                        <span class="bi bi-x clean-filter" @click="cleanFilterAndApply('status')"></span>
                    </span>
                </li>
            </ul>
        </template>

    </DataTable>

</template>

<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import OrgCreate from "./OrgCreate.vue";
import AppModal from "@/vue/shared/AppModal.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import useDates from "@/vue/composables/useDates";
import OrgMigrate from "./OrgMigrate.vue";
import OrgCreateMigratePatients from "./migrations/OrgCreateMigratePatients.vue";
import axios from "axios";
import DataTable from "@/vue/datatable/DataTable.vue";
import { Column } from "@/vue/types/datatable/column";
import { Org } from "@/vue/types/org";
import { cloneDeep } from "lodash";
import AppOffcanvas from "@/vue/shared/AppOffcanvas.vue";

const { getFormattedDate, formatDateTime } = useDates("MM/DD/YYYY");

const defaultFilters = {
    status: "",
    type: ""
};

const formValues = ref(cloneDeep(defaultFilters));
const appliedFilters = ref(cloneDeep(defaultFilters));
const isFiltersVisible = ref<boolean>(false);
const isAddOrganizationVisible = ref<boolean>(false);
const isSavingOrganization = ref<boolean>(false);
const dtKey = ref<number>(0);
const orgTypes = ref([]);
const showMigrate = ref<boolean>(false);
const showPatientMigrate = ref<boolean>(false);
const newLocationData = ref(null);
const receivedActivePatients = ref([]);
const receivedInactivePatients = ref([]);
const migrationComplete = ref<boolean>(false);
const patientModalRef = ref(null);
const orgModalRef = ref(null);
const orgCreate = ref<{
    createOrganization: () => void;
}>();

const filtersCount = computed(() => {
    const ignoredFields = [];

    return Object.keys(appliedFilters.value).reduce((count, key) => {
        const appliedValue = appliedFilters.value[key];
        const defaultValue = defaultFilters[key];

        let isDifferent;

        if (ignoredFields.includes(key)) {
            isDifferent = false; // Ignored fields aren't considered a filter
        } else if (typeof appliedValue === "object") {
            isDifferent = JSON.stringify(appliedValue) !== JSON.stringify(defaultValue);
        } else {
            isDifferent = appliedValue !== defaultValue;
        }

        return count + (isDifferent ? 1 : 0);
    }, 0);
});

const toggleAdd = () => {
    isAddOrganizationVisible.value = !isAddOrganizationVisible.value;
};

const toggleSaving = () => {
    isSavingOrganization.value = !isSavingOrganization.value;
};

const toggleMigrate = () => {
    showMigrate.value = !showMigrate.value;
};

const closeAllModals = () => {
    showPatientMigrate.value = false;
    showMigrate.value = false;
    isAddOrganizationVisible.value = false;
};

const closeOrgModal = () => {
    orgModalRef.value.closeModal();
};

const organizationCreated = () => {
    dtKey.value += 1;
    isAddOrganizationVisible.value = false;

    if (receivedActivePatients.value.length === 0 && receivedInactivePatients.value.length === 0) {
        migrationComplete.value = false;
    } else {
        showPatientMigrate.value = true;
        migrationComplete.value = true;
    }
};

const receivePatientData = (patientData) => {
    receivedActivePatients.value = patientData.activePatients;
    receivedInactivePatients.value = patientData.inactivePatients;
};

const submitOrgForm = () => {
    orgCreate.value?.createOrganization();
};

const columns: ComputedRef<Column<Org>[]> = computed(() => {
    return [
        {
            name: "name",
            title: $t("Name"),
            render: (value, row) => `<a href="/orgs/${row.id}">${value}</a>`
        },
        {
            name: "type",
            title: $t("Type"),
            render: (value, row) => row.is_clinical ? `${value} (${row.clinical_type})` : value

        },
        {
            name: "created_at",
            title: $t("Created at"),
            render: (value) => value ? formatDateTime(value, "MM/DD/YYYY HH:mm:ss") : ""
        },
        { name: "city_state", title: $t("City / State") },
        { name: "zip", title: $t("ZIP") },
        {
            name: "status",
            title: $t("Status"),
            render: (value, row) => row.status ? `<span class="badge bg-success">${$t("Active")}</span>` : `<span class="badge bg-danger">${$t("Inactive")}</span>`
        },
        { name: "patient_count", title: $t("Patients") },
        { name: "user_count", title: $t("Users") },
        { name: "location", title: $t("Primary Location") },
        {
            name: "location_count",
            title: $t("Locations"),
            render: (value, row) => `${value}
            <a
                class="ms-1 btn btn-white btn-xs"
                href="/orgs/${row.id}#locations"
                target="_blank"
            >
                <i class="me-1 bi-eye-fill"></i>
                View
            </a>`
        }
    ];
});

const updateLocation = (data) => {
    newLocationData.value = data;
};

function cleanForm() {
    formValues.value = cloneDeep(defaultFilters);
}

function resetFilters() {
    cleanForm();
    applyFilters();
}

function applyFilters() {
    appliedFilters.value = cloneDeep(formValues.value);
    isFiltersVisible.value = false;
}

function cleanFilterAndApply(filterName) {
    cleanFilter(filterName);

    applyFilters();
}

function cleanFilter(filterName) {
    const clean = (name) => {
        if (typeof defaultFilters[name] === "object" && defaultFilters[name] !== null) {
            return cloneDeep(defaultFilters[name]);
        }

        return defaultFilters[name];
    };

    if (Array.isArray(filterName)) {
        filterName.forEach((name) => {
            formValues.value[name] = clean(name);
        });
    } else {
        formValues.value[filterName] = clean(filterName);
    }
}

onMounted(() => {
    axios.get("/api/orgs/orgtypes").then(response => {
        orgTypes.value = response.data;
    });
});
</script>
